import React, { useEffect, useState } from "react";
import { Sparkles, RefreshCw, Loader2, TrendingUp, Users, Clock, CheckCircle2 } from "lucide-react";
import Button from "../ui/Button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/Card";

const TrialStatsTab = () => {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [windowDays, setWindowDays] = useState(30);

  const fetchStats = async (d = windowDays) => {
    try {
      setLoading(true);
      const r = await fetch(`${API}/api/admin/trial-stats?days=${d}`, { credentials: "include" });
      if (r.ok) setStats(await r.json());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Loader2 className="w-10 h-10 text-gold animate-spin mx-auto" />
        </CardContent>
      </Card>
    );
  }

  if (!stats) return null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h2 className="font-heading text-2xl text-gold flex items-center">
            <Sparkles className="w-6 h-6 mr-2" />
            Statistiche Premium Trial
          </h2>
          <p className="text-sm text-muted-foreground">
            Analisi degli ultimi {stats.window_days} giorni
          </p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <select
            value={windowDays}
            onChange={(e) => {
              const d = parseInt(e.target.value);
              setWindowDays(d);
              fetchStats(d);
            }}
            className="px-3 py-1.5 bg-background border border-border rounded text-sm text-foreground"
            data-testid="trial-stats-window-select"
          >
            <option value={7}>Ultimi 7 giorni</option>
            <option value={30}>Ultimi 30 giorni</option>
            <option value={90}>Ultimi 90 giorni</option>
            <option value={365}>Ultimo anno</option>
          </select>
          <Button onClick={() => fetchStats()} variant="ghost" size="sm">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs uppercase text-muted-foreground">Trial Richiesti</p>
              <Users className="w-4 h-4 text-arcane" />
            </div>
            <p className="font-heading text-3xl text-arcane" data-testid="stat-total-trials">
              {stats.total_trials}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs uppercase text-muted-foreground">Conversioni Pagate</p>
              <CheckCircle2 className="w-4 h-4 text-gold" />
            </div>
            <p className="font-heading text-3xl text-gold" data-testid="stat-converted">
              {stats.converted}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs uppercase text-muted-foreground">Conversion Rate</p>
              <TrendingUp className="w-4 h-4 text-gold" />
            </div>
            <p className="font-heading text-3xl text-gold" data-testid="stat-conversion-rate">
              {stats.conversion_rate}%
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs uppercase text-muted-foreground">Trial Attivi</p>
              <Clock className="w-4 h-4 text-arcane" />
            </div>
            <p className="font-heading text-3xl text-arcane" data-testid="stat-active-trials">
              {stats.active_trials}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Codici non usati: {stats.unused_codes}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Recent users table */}
      <Card>
        <CardHeader>
          <CardTitle>Utenti Recenti ({stats.recent_users.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {stats.recent_users.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">Nessun trial nel periodo selezionato.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs uppercase text-muted-foreground">
                    <th className="py-2 pr-4">Utente</th>
                    <th className="py-2 pr-4">Email</th>
                    <th className="py-2 pr-4">Data Richiesta</th>
                    <th className="py-2 pr-4">Codice</th>
                    <th className="py-2 pr-4">Riscattato</th>
                    <th className="py-2 pr-4">Convertito a Pagante</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recent_users.map((u) => (
                    <tr key={u.user_id} className="border-b border-border/40" data-testid={`trial-user-${u.user_id}`}>
                      <td className="py-3 pr-4 font-heading text-foreground">{u.name || "—"}</td>
                      <td className="py-3 pr-4 text-muted-foreground">{u.email}</td>
                      <td className="py-3 pr-4 text-muted-foreground">
                        {new Date(u.claimed_at).toLocaleDateString("it-IT")}
                      </td>
                      <td className="py-3 pr-4 font-mono text-xs text-gold">{u.code_display || "—"}</td>
                      <td className="py-3 pr-4">
                        {u.code_used ? (
                          <span className="px-2 py-0.5 bg-arcane/20 text-arcane text-xs rounded font-heading">SÌ</span>
                        ) : (
                          <span className="px-2 py-0.5 bg-muted/20 text-muted-foreground text-xs rounded font-heading">NO</span>
                        )}
                      </td>
                      <td className="py-3 pr-4">
                        {u.converted_to_paid ? (
                          <span className="px-2 py-0.5 bg-gold/20 text-gold text-xs rounded font-heading">SÌ</span>
                        ) : (
                          <span className="px-2 py-0.5 bg-muted/20 text-muted-foreground text-xs rounded font-heading">NO</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TrialStatsTab;
